import React, { useCallback, useEffect, useState } from "react";
import { Loader2, RefreshCw, Download, FileText, CheckCircle2, XCircle, Clock } from "lucide-react";
import api from "../../../lib/api";
import { Button } from "../../ui/button";
import { Textarea } from "../../ui/textarea";
import { toast } from "sonner";

const BAND = {
  high: "bg-red-100 text-red-700",
  medium: "bg-amber-100 text-amber-700",
  low: "bg-slate-100 text-slate-600",
};

const STATUS_LABEL = { open: "Open", approved: "Approved", rejected: "Rejected", deferred: "Deferred", done: "Done" };

// The three kinds of finding call for different work, so they are named rather
// than mixed into one list.
const TYPE_LABEL = {
  visibility: "Visibility opportunity",
  citation: "Citation opportunity",
  accuracy: "Accuracy risk",
};
const TYPE_STYLE = {
  visibility: "bg-indigo-100 text-indigo-700",
  citation: "bg-teal-100 text-teal-700",
  accuracy: "bg-red-100 text-red-700",
};

const when = (iso) => (iso ? new Date(iso).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" }) : "—");

function Recommendation({ rec, onDecide, onDraft, busy }) {
  const [open, setOpen] = useState(false);
  const [instruction, setInstruction] = useState("");
  const p = rec.priority || {};

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid={`ai-rec-${rec.id}`}>
      <div className="flex flex-wrap items-start gap-2">
        {rec.finding_type && (
          <span className={`rounded-full px-2 py-0.5 text-[11px] font-bold ${TYPE_STYLE[rec.finding_type] || BAND.low}`}
                data-testid={`ai-rec-type-${rec.id}`}>
            {TYPE_LABEL[rec.finding_type]}
          </span>
        )}
        <span className={`rounded-full px-2 py-0.5 text-[11px] font-bold ${BAND[p.band] || BAND.low}`}>
          {p.band} priority
        </span>
        {rec.status !== "open" && (
          <span className="rounded-full bg-sky-100 px-2 py-0.5 text-[11px] font-bold text-sky-700">
            {STATUS_LABEL[rec.status]}
          </span>
        )}
        <h4 className="font-bold text-navy text-sm flex-1 min-w-[240px]">{rec.title}</h4>
        <button onClick={() => setOpen(!open)} className="text-xs font-semibold text-slate-500 hover:text-navy"
                data-testid={`ai-rec-toggle-${rec.id}`}>
          {open ? "Hide" : "Evidence and actions"}
        </button>
      </div>

      <p className="text-sm text-slate-600 mt-2 leading-relaxed">{rec.detail}</p>
      <p className="text-xs text-slate-500 mt-2">
        {rec.area} · impact {p.impact}/10 · {p.confidence} confidence · {p.effort} effort
        {rec.times_seen > 1 ? ` · seen in ${rec.times_seen} cycles` : ""}
      </p>

      {open && (
        <div className="mt-4 space-y-3 border-t border-slate-100 pt-3">
          <div>
            <p className="text-xs font-bold text-navy">Recommended action</p>
            <p className="text-sm text-slate-600 mt-1">{rec.action}</p>
          </div>
          <div>
            <p className="text-xs font-bold text-navy">Expected benefit</p>
            <p className="text-sm text-slate-600 mt-1">{rec.expected_benefit}</p>
          </div>
          <div>
            <p className="text-xs font-bold text-navy">Evidence</p>
            <pre className="mt-1 whitespace-pre-wrap rounded-lg bg-slate-50 p-3 text-[11px] text-slate-600 max-h-56 overflow-y-auto">
              {JSON.stringify(rec.evidence, null, 1)}
            </pre>
          </div>

          {rec.draft ? (
            <div className="rounded-lg border border-sky-200 bg-sky-50 p-3" data-testid={`ai-rec-draft-${rec.id}`}>
              <p className="text-xs font-bold text-navy">Proposed amendment — not published</p>
              <p className="text-sm font-semibold text-navy mt-2">{rec.draft.heading}</p>
              <pre className="mt-1 whitespace-pre-wrap text-xs text-slate-700 max-h-72 overflow-y-auto">{rec.draft.body}</pre>
              <p className="text-[11px] text-slate-500 mt-2">
                {rec.draft.notes} · drafted {when(rec.draft.generated_at)}. Copy it into the page editor and publish
                it the usual way — nothing here changes the live site.
              </p>
            </div>
          ) : null}

          <div className="flex flex-wrap items-center gap-2">
            <Button size="sm" variant="outline" onClick={() => onDecide(rec.id, "approved")} disabled={busy}
                    data-testid={`ai-rec-approve-${rec.id}`}>
              <CheckCircle2 size={13} className="mr-1.5" /> Approve
            </Button>
            <Button size="sm" variant="outline" onClick={() => onDecide(rec.id, "deferred")} disabled={busy}
                    data-testid={`ai-rec-defer-${rec.id}`}>
              <Clock size={13} className="mr-1.5" /> Defer
            </Button>
            <Button size="sm" variant="outline" onClick={() => onDecide(rec.id, "rejected")} disabled={busy}
                    data-testid={`ai-rec-reject-${rec.id}`}>
              <XCircle size={13} className="mr-1.5" /> Reject
            </Button>
            <Button size="sm" variant="outline" onClick={() => onDecide(rec.id, "done")} disabled={busy}
                    data-testid={`ai-rec-done-${rec.id}`}>
              Mark as done
            </Button>
            <Button size="sm" onClick={() => onDraft(rec.id, instruction)} disabled={busy}
                    data-testid={`ai-rec-draft-btn-${rec.id}`}>
              <FileText size={13} className="mr-1.5" /> {rec.draft ? "Redraft" : "Draft an amendment"}
            </Button>
          </div>
          <Textarea rows={2} value={instruction} onChange={(e) => setInstruction(e.target.value)}
                    placeholder="Anything the draft should take into account (optional)"
                    data-testid={`ai-rec-instruction-${rec.id}`} />

          {(rec.history || []).length > 0 && (
            <div className="text-[11px] text-slate-500">
              {rec.history.map((h, i) => (
                <p key={i}>{STATUS_LABEL[h.status] || h.status} by {h.by || "system"} on {when(h.at)}{h.note ? ` — ${h.note}` : ""}</p>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function ImprovementOpportunities() {
  const [data, setData] = useState(null);
  const [history, setHistory] = useState(null);
  const [busy, setBusy] = useState(false);
  const [filter, setFilter] = useState("open");
  const [typeFilter, setTypeFilter] = useState("");

  const load = useCallback(() => {
    api.get("/admin/ai-monitor/recommendations").then((r) => setData(r.data)).catch(() => setData(null));
    api.get("/admin/ai-monitor/recommendations/history").then((r) => setHistory(r.data)).catch(() => setHistory(null));
  }, []);

  useEffect(() => { load(); }, [load]);

  const refresh = async () => {
    setBusy(true);
    try {
      const res = await api.post("/admin/ai-monitor/recommendations/refresh", {});
      toast.success(res.data.detail);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "The opportunities could not be refreshed.");
    } finally {
      setBusy(false);
    }
  };

  const decide = async (id, status) => {
    setBusy(true);
    try {
      await api.patch("/admin/ai-monitor/recommendations", { id, status });
      toast.success(status === "done" ? "Marked as done — the current figures are kept as the baseline." : "Updated.");
      load();
    } finally {
      setBusy(false);
    }
  };

  const draft = async (id, instruction) => {
    setBusy(true);
    try {
      const res = await api.post("/admin/ai-monitor/recommendations/draft", { id, instruction });
      toast.success(res.data.detail);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "The draft could not be generated.");
    } finally {
      setBusy(false);
    }
  };

  const exportAll = (fmt) => {
    const base = process.env.REACT_APP_BACKEND_URL;
    const token = localStorage.getItem("cass_token");
    fetch(`${base}/api/admin/ai-monitor/export?fmt=${fmt}`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.blob())
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `cass-ai-visibility.${fmt}`;
        a.click();
        URL.revokeObjectURL(url);
      })
      .catch(() => toast.error("The export could not be downloaded."));
  };

  if (!data) return <p className="text-sm text-slate-500">Loading…</p>;

  const visible = (data.recommendations || [])
    .filter((r) => filter === "all" || r.status === filter)
    .filter((r) => !typeFilter || r.finding_type === typeFilter);

  return (
    <div className="space-y-5" data-testid="ai-improvements">
      <div className="rounded-xl border border-slate-200 bg-white p-4">
        <div className="flex flex-wrap items-center gap-2">
          <Button size="sm" onClick={refresh} disabled={busy} data-testid="ai-rec-refresh">
            {busy ? <Loader2 className="animate-spin mr-2" size={14} /> : <RefreshCw className="mr-2" size={14} />}
            Find opportunities
          </Button>
          <Button size="sm" variant="outline" onClick={() => exportAll("json")} data-testid="ai-export-json">
            <Download size={13} className="mr-1.5" /> Export everything (JSON)
          </Button>
          <Button size="sm" variant="outline" onClick={() => exportAll("csv")} data-testid="ai-export-csv">
            <Download size={13} className="mr-1.5" /> Responses (CSV)
          </Button>
          <span className="text-xs text-slate-500 ml-auto">
            {data.last_refresh ? `Last looked ${when(data.last_refresh)}` : "Not yet run"}
          </span>
        </div>
        <div className="mt-3 flex flex-wrap gap-2">
          {["open", "approved", "deferred", "rejected", "done", "all"].map((s) => (
            <button key={s} onClick={() => setFilter(s)} data-testid={`ai-rec-filter-${s}`}
                    className={`px-3 py-1.5 rounded-full text-xs font-semibold border ${
                      filter === s ? "bg-navy text-white border-navy" : "bg-white text-slate-600 border-slate-200"}`}>
              {STATUS_LABEL[s] || "All"}{s !== "all" ? ` ${data.counts?.[s] ?? 0}` : ""}
            </button>
          ))}
        </div>
        <div className="mt-3 flex flex-wrap gap-2" data-testid="ai-rec-type-filters">
          {[["", "Every kind"], ["visibility", "Visibility"], ["citation", "Citation"], ["accuracy", "Accuracy"]]
            .map(([k, label]) => (
              <button key={k || "all"} onClick={() => setTypeFilter(k)} data-testid={`ai-rec-type-filter-${k || "all"}`}
                      className={`px-3 py-1.5 rounded-full text-xs font-semibold border ${
                        typeFilter === k ? "bg-slate-800 text-white border-slate-800" : "bg-white text-slate-600 border-slate-200"}`}>
                {label}{k ? ` ${data.by_type?.[k] ?? 0}` : ""}
              </button>
            ))}
        </div>
        <p className="text-xs text-slate-500 mt-3">
          {data.types ? Object.entries(data.types).map(([k, v]) => `${TYPE_LABEL[k]}: ${v.toLowerCase()}`).join(" · ") : ""}
        </p>
        <p className="text-xs text-slate-500 mt-2">{data.note}</p>
      </div>

      {visible.length === 0 ? (
        <p className="text-sm text-slate-600" data-testid="ai-improvements-empty">
          Nothing in this list. Press "Find opportunities" after a monitoring run — the evidence comes from the
          answers, Search Console, Ask Cassie and the enquiry figures.
        </p>
      ) : (
        <div className="space-y-3">
          {visible.map((rec) => (
            <Recommendation key={rec.id} rec={rec} onDecide={decide} onDraft={draft} busy={busy} />
          ))}
        </div>
      )}

      {(history?.history || []).length > 0 && (
        <section className="rounded-xl border border-slate-200 bg-white p-4" data-testid="ai-action-history">
          <h4 className="font-bold text-navy text-sm">Action history</h4>
          <p className="text-xs text-slate-500 mt-1">{history.note}</p>
          <table className="min-w-full text-sm mt-3">
            <thead className="text-slate-600"><tr>
              <th className="text-left py-1">Change</th><th className="text-left py-1">When</th>
              <th className="text-left py-1">Recommended then</th><th className="text-left py-1">Now</th>
            </tr></thead>
            <tbody>
              {history.history.map((h) => (
                <tr key={h.id} className="border-t border-slate-100">
                  <td className="py-1.5 pr-3 text-slate-700">{h.title}</td>
                  <td className="py-1.5 pr-3 text-slate-500">{when(h.completed_at)}</td>
                  <td className="py-1.5 pr-3">
                    {h.movement?.recommended?.then !== null && h.movement?.recommended?.then !== undefined
                      ? `${(h.movement.recommended.then * 100).toFixed(0)}%` : "—"}
                  </td>
                  <td className="py-1.5 pr-3">
                    {h.movement?.recommended?.now !== null && h.movement?.recommended?.now !== undefined
                      ? `${(h.movement.recommended.now * 100).toFixed(0)}%` : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}
    </div>
  );
}
